import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebase';
import {
  getAcademicYearAndSemester,
  type Timetable,
  type TimetableEntry,
} from './neis';

/** 컴시간 callable은 asia-northeast3에 배포됨 (us-central1은 IAM 미개방으로 403) */
const FUNCTIONS_REGION = 'asia-northeast3';

export interface ComciganSchool {
  regionCode: number;
  region: string;
  name: string;
  code: number;
}

interface SearchResponse {
  schools: ComciganSchool[];
}

interface FetchResponse {
  schoolName: string;
  regionName: string;
  startDate: string;
  lastUpdated: string;
  year: number;
  entries: TimetableEntry[];
}

const getCallableErrorMessage = (error: unknown, fallback: string): string => {
  if (!error || typeof error !== 'object') return fallback;

  const code = 'code' in error ? String((error as { code?: string }).code || '') : '';
  const message = 'message' in error ? String((error as { message?: string }).message || '').trim() : '';
  const details = 'details' in error ? (error as { details?: unknown }).details : undefined;
  const detailMessage =
    details && typeof details === 'object' && details !== null && 'message' in details
      ? String((details as { message?: string }).message || '').trim()
      : '';

  // Firebase SDK가 권한/내부 오류를 짧게 "internal" 등으로만 내려주는 경우
  if (code.includes('permission-denied') || /forbidden|permission/i.test(message)) {
    return '컴시간 함수 호출 권한이 없습니다. 배포 리전/IAM 설정을 확인해 주세요.';
  }
  if (code.includes('not-found')) {
    return '컴시간 함수를 찾을 수 없습니다. 배포 상태를 확인해 주세요.';
  }
  if (code.includes('unauthenticated')) {
    return detailMessage || message || '로그인이 필요합니다.';
  }
  if (code.includes('unavailable') || code.includes('deadline-exceeded')) {
    return detailMessage || message || '컴시간 서버에 일시적으로 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.';
  }

  // "internal" 같은 짧은 코드성 메시지는 사용자용으로 바꿈
  if (!message || /^(internal|unknown|error)$/i.test(message)) {
    return detailMessage || fallback;
  }
  return message;
};

export async function searchComciganSchools(query: string): Promise<ComciganSchool[]> {
  const functions = getFunctions(app, FUNCTIONS_REGION);
  const search = httpsCallable<{ query: string }, SearchResponse>(functions, 'searchComciganSchools');
  try {
    const result = await search({ query: query.trim() });
    return result.data.schools || [];
  } catch (error) {
    console.error('컴시간 학교 검색 실패:', error);
    throw new Error(getCallableErrorMessage(error, '컴시간에서 학교를 검색하지 못했습니다.'));
  }
}

export async function fetchComciganTimetable(params: {
  schoolCode: number;
  grade: number;
  classNum: number;
  week?: 0 | 1;
}): Promise<Timetable> {
  const functions = getFunctions(app, FUNCTIONS_REGION);
  const fetchTimetable = httpsCallable<
    { schoolCode: number; grade: number; classNum: number; week?: 0 | 1 },
    FetchResponse
  >(functions, 'fetchComciganTimetable');

  try {
    const result = await fetchTimetable({
      schoolCode: params.schoolCode,
      grade: params.grade,
      classNum: params.classNum,
      week: params.week ?? 0,
    });
    const academic = getAcademicYearAndSemester();
    return {
      semester: academic.semester === 1 ? '1학기' : '2학기',
      year: result.data.year || academic.year,
      entries: result.data.entries || [],
    };
  } catch (error) {
    console.error('컴시간 시간표 조회 실패:', error);
    throw new Error(getCallableErrorMessage(error, '컴시간에서 시간표를 불러오지 못했습니다.'));
  }
}
